import type {
  CsvDerivedColumn,
  TableVisualizationColumn,
} from "~/ui-rtk/constants/table-visualization-types"

/**
 * One CSV column produced for a funnel-table UI column. `member` reads a Cube
 * field of the exported row; `derived` computes a percentage from two of
 * them (the conversion / abandon rates the tables calculate client-side).
 */
export type FunnelExportColumn = {
  header: string
  member?: string
  /** Member read instead when `member` is empty on the row (title → handle). */
  fallbackMember?: string
  derived?: CsvDerivedColumn
}

/** UI column key → the CSV column(s) it exports as. Unlisted keys are skipped. */
export type FunnelExportMapping = Record<string, FunnelExportColumn[]>

/**
 * Builds the column config for the worker CSV export from the columns the
 * user has visible, in their order. The funnel tables show camelCase keys
 * mapped from Cube fields and rates computed on the client, neither of which
 * the worker can read off a raw Cube row — this translates each visible
 * column into the raw member it comes from, or a derived percentage.
 */
export function buildFunnelExportColumns(
  visibleColumns: TableVisualizationColumn[],
  mapping: FunnelExportMapping,
): TableVisualizationColumn[] {
  return visibleColumns.flatMap(column => {
    const specs = mapping[column.key]
    if (!specs) return []
    return specs.map((spec, index) => ({
      key: spec.member ?? `${column.key}${index > 0 ? `_${index}` : ""}`,
      header: spec.header,
      type: column.type ?? "auto",
      ...(spec.derived ? { csvDerived: spec.derived } : {}),
      ...(spec.fallbackMember ? { csvFallbackKey: spec.fallbackMember } : {}),
    }))
  })
}

/** "Add to Cart Conversion Rate" → "Add to Cart Abandon Rate", "Checkout Rate" → "Checkout Abandon Rate". */
function abandonLabel(label: string): string {
  if (/Conversion Rate$/.test(label)) {
    return label.replace(/Conversion Rate$/, "Abandon Rate")
  }
  if (/ Rate$/.test(label)) return label.replace(/ Rate$/, " Abandon Rate")
  return `${label} Abandon Rate`
}

/** Conversion rate + abandon rate pair for a funnel step. */
export function rateColumns(
  label: string,
  numerator: string,
  denominator: string,
  { withAbandon = true }: { withAbandon?: boolean } = {},
): FunnelExportColumn[] {
  return [
    { header: `${label} (%)`, derived: { numerator, denominator } },
    ...(withAbandon
      ? [
          {
            header: `${abandonLabel(label)} (%)`,
            derived: { numerator, denominator, complement: true },
          },
        ]
      : []),
  ]
}
